import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { cva } from "class-variance-authority";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const BRAND_ORANGE = "#FC652E";
const BRAND_RED_GUM = "#892102";
const BRAND_WHITE = "#FFFFFF";

/** Normalize #RGB / #RRGGBB → lowercase 6-char hex without # */
export function normalizeHex(hex) {
  if (!hex || typeof hex !== "string") return "";
  let value = hex.trim().replace(/^#/, "");
  if (value.length === 3) {
    value = value
      .split("")
      .map((char) => char + char)
      .join("");
  }
  return /^[0-9a-fA-F]{6}$/.test(value) ? value.toLowerCase() : "";
}

export function isRedGumHex(hex) {
  return normalizeHex(hex) === "892102";
}

/** Hex (#RGB / #RRGGBB) → space-separated RGB channels for CSS vars */
export function hexToRgbChannels(hex) {
  const value = normalizeHex(hex);
  if (!value) return undefined;
  const num = parseInt(value, 16);
  return `${(num >> 16) & 255} ${(num >> 8) & 255} ${num & 255}`;
}

/** Red gum (#892102) ↔ saddle orange (#FC652E) for primary button hover */
export function resolvePrimaryButtonHoverHex(backgroundHex) {
  if (isRedGumHex(backgroundHex)) return BRAND_ORANGE;
  return BRAND_RED_GUM;
}

/**
 * Solid red gum buttons (and red gum hover fills) always use white labels.
 * @param {string|undefined} backgroundHex
 * @param {string|undefined} foregroundHex
 */
export function resolveButtonForegroundHex(backgroundHex, foregroundHex) {
  if (isRedGumHex(backgroundHex)) return BRAND_WHITE;
  return foregroundHex || BRAND_WHITE;
}

/**
 * Build inline CSS var overrides for theme button tokens.
 * Only applies when custom colours are provided — otherwise parent/theme
 * `--button-*` vars are used (e.g. red gum on competitions FilterBar).
 */
export function buttonColourStyle(customColors) {
  if (!customColors?.background && !customColors?.foreground) {
    return undefined;
  }

  const style = {};
  const backgroundHex = customColors.background || BRAND_ORANGE;
  const hoverHex = resolvePrimaryButtonHoverHex(backgroundHex);
  const foregroundHex = resolveButtonForegroundHex(
    backgroundHex,
    customColors.foreground
  );
  const hoverForegroundHex = isRedGumHex(hoverHex)
    ? BRAND_WHITE
    : foregroundHex;

  const bg = hexToRgbChannels(backgroundHex);
  const fg = hexToRgbChannels(foregroundHex);
  const hover = hexToRgbChannels(hoverHex);
  const hoverFg = hexToRgbChannels(hoverForegroundHex);

  if (bg) style["--button-background"] = bg;
  if (fg) style["--button-foreground"] = fg;
  if (hover) style["--button-hover-background"] = hover;
  if (hoverFg) style["--button-hover-foreground"] = hoverFg;
  return Object.keys(style).length ? style : undefined;
}

/**
 * @deprecated Do not use for CMS titles/copy — render CMS strings as entered.
 * Kept only if a non-CMS UI label needs it.
 */
export function toSentenceCase(label) {
  if (!label || typeof label !== "string") return label;
  const trimmed = label.trim();
  if (!trimmed) return label;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/**
 * @deprecated Do not use for CMS titles/copy — render CMS strings as entered.
 */
export function toProperCase(label) {
  if (!label || typeof label !== "string") return label;
  const trimmed = label.trim();
  if (!trimmed) return label;
  // Split on spaces/hyphens/slashes only — not apostrophes ("What's", not "What'S")
  return trimmed
    .toLowerCase()
    .replace(/(^|[\s\-/])(\S)/g, (_, sep, char) => sep + char.toUpperCase());
}

/** Statamic 6 LinkValue objects or legacy string URLs → href string */
export function resolveLink(value) {
  if (value == null || value === "") return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof value.url === "string" && value.url) {
    return value.url;
  }
  return undefined;
}

// TEST

// export const backgroundColor = {
//   "#EC008C":
//     "bg-[#EC008C] text-white bg-gradient-to-br from-[#ff50b8] to-[#cf007b]",
//   "#00AEEF": "bg-[#00AEEF] text-white",
//   "#001F75": "bg-[#001F75] text-white",
//   "#0068A4": "bg-[#0068A4] text-white",
//   "#58595B": "bg-[#58595B] text-white",
//   "#E71A29": "bg-[#E71A29] text-white",
//   "#002F6C": "bg-[#002F6C] text-white",
//   "#2E3192": "bg-[#2E3192] text-white",
//   "#BD0088": "bg-[#BD0088] text-white",
//   "#2EA537": "bg-[#2EA537] text-white",
//   "#BDC4C3": "bg-[#BDC4C3] text-black",
//   "#FFFFFF": "bg-[#FFFFFF] text-black",
//   "#000000": "bg-[#000000] text-white",
//   "#ec008c": "bg-[#EC008C] text-white",
//   "#00aeef": "bg-[#00AEEF] text-white",
//   "#001f75": "bg-[#001F75] text-white",
//   "#0068a4": "bg-[#0068A4] text-white",
//   "#58595b": "bg-[#58595B] text-white",
//   "#e71a29": "bg-[#E71A29] text-white",
//   "#002f6c": "bg-[#002F6C] text-white",
//   "#2e3192": "bg-[#2E3192] text-white",
//   "#bd0088": "bg-[#BD0088] text-white",
//   "#2ea537": "bg-[#2EA537] text-white",
//   "#bdc4c3": "bg-[#BDC4C3] text-black",
//   "#ffffff": "bg-[#FFFFFF] text-black",
//   "#000000": "bg-[#000000] text-white",
//   "#32384B":
//     "bg-[#32384B] text-white bg-gradient-to-br from-[#505976] to-[#232838]",
//   default: "bg-background text-foreground",
// };

export const textColor = {
  "#EC008C": "text-[#EC008C]",
  "#00AEEF": "text-[#00AEEF]",
  "#001F75": "text-[#001F75]",
  "#0068A4": "text-[#0068A4]",
  "#58595B": "text-[#58595B]",
  "#E71A29": "text-[#E71A29]",
  "#002F6C": "text-[#002F6C]",
  "#2E3192": "text-[#2E3192]",
  "#BD0088": "text-[#BD0088]",
  "#2EA537": "text-[#2EA537]",
  "#BDC4C3": "text-[#BDC4C3]",
  "#FFFFFF": "text-[#FFFFFF]",
  "#000000": "text-[#000000]",
  "#ec008c": "text-[#EC008C]",
  "#00aeef": "text-[#00AEEF]",
  "#001f75": "text-[#001F75]",
  "#0068a4": "text-[#0068A4]",
  "#58595b": "text-[#58595B]",
  "#e71a29": "text-[#E71A29]",
  "#002f6c": "text-[#002F6C]",
  "#2e3192": "text-[#2E3192]",
  "#bd0088": "text-[#BD0088]",
  "#2ea537": "text-[#2EA537]",
  "#bdc4c3": "text-[#BDC4C3]",
  "#ffffff": "text-[#FFFFFF]",
  "#000000": "text-[#000000]",
};

export const imageStyles = cva("w-full lg:rounded-r-lg object-cover", {
  variants: {
    removePadding: {
      true: "h-full",
      false: "lg:mt-6 lg:p-12 p-16",
    },
  },
});

export const rightSideContainerStyles = cva(
  "h-[300px] lg:h-full aspect-auto overflow-hidden relative rounded-t-lg lg:rounded-none",
  {
    variants: {
      removePadding: {
        true: "",
        false: "lg:p-12 p-6",
      },
    },
  }
);

export const priceContainerStyles = cva("flex items-center justify-end", {
  variants: {
    hidden: {
      true: "hidden",
      false: "",
    },
  },
  defaultVariants: {
    hidden: false,
  },
});

export const allButtonStyles = cva(
  "h-12 px-8 py-2 flex flex-col items-center rounded-button-radius justify-center leading-4 text-base font-button font-semibold",
  {
    variants: {
      status: {
        selected:
          "bg-button border-2 border-button lg:hover:bg-button text-button-foreground lg:hover:text-button-foreground",
        unselected:
          "bg-brand-wool border-2 border-brand-fawn text-button lg:hover:bg-button lg:hover:text-button-foreground lg:hover:border-button",
      },
    },
    defaultVariants: {
      status: "unselected",
    },
  }
);

export const buttonStyles = cva(
  "h-12 px-8 py-2 flex flex-col items-center rounded-button-radius justify-center leading-4 text-base font-button font-semibold",
  {
    variants: {
      status: {
        selected:
          "bg-button border-2 border-button lg:hover:bg-button text-button-foreground lg:hover:text-button-foreground",
        unselected:
          "bg-brand-wool border-2 border-brand-fawn text-button lg:hover:bg-button lg:hover:text-button-foreground lg:hover:border-button",
      },
    },
    defaultVariants: {
      status: "unselected",
    },
  }
);

export const mapButtonStyles = cva(
  "h-12 px-4 py-1 flex flex-col items-center justify-center leading-4 text-sm font-button font-semibold rounded-lg",
  {
    variants: {
      status: {
        selected:
          "bg-button border border-button lg:hover:bg-button-hover text-button-foreground lg:hover:text-button-hover-foreground",
        unselected:
          "border border-foreground text-foreground lg:hover:border-2",
      },
    },
    defaultVariants: {
      status: "unselected",
    },
  }
);

export const imageContainerStyles = cva(
  "relative overflow-hidden w-full rounded-base flex mx-auto justify-center items-center",
  {
    variants: {
      height: {
        sm: "h-[200px] lg:h-[300px]",
        md: "h-[300px] lg:h-[500px]",
        lg: "h-[500px] lg:h-[700px]",
        none: "",
      },
      width: {
        sm: "w-[300px] lg:w-[400px]",
        md: "w-[500px] lg:w-[900px]",
        lg: "w-[90%] lg:w-[1200px]",
        full: "w-full",
      },
    },
    defaultVariants: {
      height: "md",
    },
  }
);
